import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/utils/api-client';

interface InquiryNotifications {
  pendingCount: number;
  totalCount: number;
  hasNewInquiries: boolean;
}

export const useInquiryNotifications = () => {
  const { isAuthenticated, isHost } = useAuth();
  const [notifications, setNotifications] = useState<InquiryNotifications>({
    pendingCount: 0,
    totalCount: 0,
    hasNewInquiries: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !isHost()) {
      setNotifications({
        pendingCount: 0,
        totalCount: 0,
        hasNewInquiries: false,
      });
      return;
    }

    try {
      setIsLoading(true);

      // Make actual API call to get inquiry notifications using the API client
      const response = await apiClient.get<InquiryNotifications>('/inquiries/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching inquiry notifications:', error);
      // Reset to default state on error
      setNotifications({
        pendingCount: 0,
        totalCount: 0,
        hasNewInquiries: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isHost]);

  // Fetch notifications on mount and when auth state changes
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refresh notifications (can be called manually)
  const refreshNotifications = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark notifications as seen
  const markAsSeen = useCallback(() => {
    setNotifications(prev => ({
      ...prev,
      hasNewInquiries: false,
    }));
  }, []);

  return {
    notifications,
    isLoading,
    refreshNotifications,
    markAsSeen,
  };
};

export default useInquiryNotifications;